import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate } from 'react-router-dom';
import {
  fetchProjectReport,
  deleteProjectReport,
  submitForApproval,
  approveReport,
  rejectReport,
} from '../api/projectReports';
import type { ProjectReport } from '../api/projectReports';
import { useUserRoles } from '../auth';

export default function ProjectReportDetail() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dateLocale = i18n.resolvedLanguage === 'de' ? 'de-DE' : 'en-GB';
  const { roles: userRoles } = useUserRoles();

  const [report, setReport] = useState<ProjectReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const hasRole = useCallback(
    (role: string) => userRoles.length === 0 || userRoles.includes('ADMIN') || userRoles.includes(role),
    [userRoles],
  );

  const canEdit = hasRole('PROJECT_ADMIN') || hasRole('EMPLOYEE');
  const canDelete = hasRole('PROJECT_ADMIN');
  const canApprove = hasRole('HR_ADMIN');

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    setError(null);
    fetchProjectReport(id)
      .then(setReport)
      .catch((err) => setError(err instanceof Error ? err.message : t('projectReports.failedToLoad')))
      .finally(() => setLoading(false));
  }, [id, t]);

  async function handleDelete() {
    if (!id || !report) return;
    if (!window.confirm(t('projectReports.detail.confirmDelete'))) return;
    setActionLoading(true);
    try {
      await deleteProjectReport(id);
      navigate('/project-reports');
    } catch {
      setError(t('projectReports.failedToLoad'));
    } finally {
      setActionLoading(false);
    }
  }

  async function handleSubmitForApproval() {
    if (!id) return;
    setActionLoading(true);
    try {
      const updated = await submitForApproval(id);
      setReport(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleApprove() {
    if (!id) return;
    setActionLoading(true);
    try {
      const updated = await approveReport(id);
      setReport(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  async function handleReject() {
    if (!id) return;
    setActionLoading(true);
    try {
      const updated = await rejectReport(id);
      setReport(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed');
    } finally {
      setActionLoading(false);
    }
  }

  function formatDate(dateStr: string | null) {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString(dateLocale, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  }

  function statusBadge(status: string) {
    const classes: Record<string, string> = {
      DRAFT: 'bg-gray-100 text-gray-700',
      PENDING_APPROVAL: 'bg-yellow-100 text-yellow-800',
      APPROVED: 'bg-green-100 text-green-700',
    };
    const labels: Record<string, string> = {
      DRAFT: t('projectReports.statusDraft'),
      PENDING_APPROVAL: t('projectReports.statusPending'),
      APPROVED: t('projectReports.statusApproved'),
    };
    return (
      <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${classes[status] ?? 'bg-gray-100 text-gray-700'}`}>
        {labels[status] ?? status}
      </span>
    );
  }

  const contractTypeLabel = (type: string | null) => {
    if (!type) return '-';
    return t(`projectReports.detail.contractTypes.${type}`, { defaultValue: type });
  };

  if (loading) {
    return (
      <div role="status" aria-live="polite" className="flex items-center gap-3 text-gray-500 py-8">
        <span className="animate-spin text-brand-600 text-xl" aria-hidden="true">&#x27F3;</span>
        {t('projectReports.loading')}
      </div>
    );
  }

  if (error) {
    return (
      <div role="alert" className="rounded-lg bg-red-50 border border-red-200 p-4 text-red-700 text-sm">
        {error}
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="space-y-6">
      {/* Top nav */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <button
          onClick={() => navigate('/project-reports')}
          className="btn-secondary text-sm"
        >
          &larr; {t('projectReports.detail.backToList')}
        </button>
        <div className="flex flex-wrap gap-2">
          {canEdit && (
            <button
              onClick={() => navigate(`/project-reports/${id}/edit`)}
              className="btn-primary text-sm"
            >
              {t('projectReports.detail.edit')}
            </button>
          )}
          {canEdit && report.status === 'DRAFT' && (
            <button
              onClick={handleSubmitForApproval}
              disabled={actionLoading}
              className="btn-primary text-sm bg-yellow-500 hover:bg-yellow-600"
            >
              {t('projectReports.detail.submitForApproval')}
            </button>
          )}
          {canApprove && report.status === 'PENDING_APPROVAL' && (
            <>
              <button
                onClick={handleApprove}
                disabled={actionLoading}
                className="btn-primary text-sm bg-green-600 hover:bg-green-700"
              >
                {t('projectReports.detail.approve')}
              </button>
              <button
                onClick={handleReject}
                disabled={actionLoading}
                className="btn-secondary text-sm text-red-600 border-red-300 hover:bg-red-50"
              >
                {t('projectReports.detail.reject')}
              </button>
            </>
          )}
          {canDelete && (
            <button
              onClick={handleDelete}
              disabled={actionLoading}
              className="btn-secondary text-sm text-red-600 border-red-300 hover:bg-red-50"
            >
              {t('projectReports.detail.delete')}
            </button>
          )}
        </div>
      </div>

      {/* Pending banner */}
      {(report.status === 'PENDING_APPROVAL' || report.has_pending_changes) && (
        <div className="rounded-lg bg-yellow-50 border border-yellow-200 px-4 py-3 text-sm text-yellow-800">
          {t('projectReports.detail.pendingBanner')}
        </div>
      )}

      {/* Header card */}
      <div className="card">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{report.title}</h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('projectReports.detail.projectId')}: {report.project_id}
            </p>
          </div>
          {statusBadge(report.status)}
        </div>
      </div>

      {/* Metadata grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Left column */}
        <div className="card space-y-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.authors')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {report.authors && report.authors.length > 0
                ? report.authors.map((a) => (
                    <span key={a.id} className="inline-flex items-center rounded-full bg-brand-50 px-2.5 py-0.5 text-xs font-medium text-brand-700">
                      {a.full_name}
                    </span>
                  ))
                : <span className="text-sm text-gray-600">{report.author_names || '-'}</span>}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.customer')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{report.customer_name ?? '-'}</dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.department')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{report.department ?? '-'}</dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.competenceField')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {report.competence_field_names && report.competence_field_names.length > 0
                ? report.competence_field_names.map((cf) => (
                    <span key={cf} className="inline-flex items-center rounded-full bg-orange-50 px-2.5 py-0.5 text-xs font-medium text-orange-700">
                      {cf}
                    </span>
                  ))
                : <span className="text-sm text-gray-400">-</span>}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.contractType')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{contractTypeLabel(report.contract_type)}</dd>
          </div>
        </div>

        {/* Right column */}
        <div className="card space-y-4">
          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.sectors')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {report.sector_names && report.sector_names.length > 0
                ? report.sector_names.map((s) => (
                    <span key={s} className="inline-flex items-center rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
                      {s}
                    </span>
                  ))
                : <span className="text-sm text-gray-400">-</span>}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.technologies')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {report.technology_names && report.technology_names.length > 0
                ? report.technology_names.map((tech) => (
                    <span key={tech} className="inline-flex items-center rounded-full bg-purple-50 px-2.5 py-0.5 text-xs font-medium text-purple-700">
                      {tech}
                    </span>
                  ))
                : <span className="text-sm text-gray-400">-</span>}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.skills')}</dt>
            <dd className="mt-1 flex flex-wrap gap-1.5">
              {report.skill_names && report.skill_names.length > 0
                ? report.skill_names.map((skill) => (
                    <span key={skill} className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-700">
                      {skill}
                    </span>
                  ))
                : <span className="text-sm text-gray-400">-</span>}
            </dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.projectStart')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(report.project_start)}</dd>
          </div>

          <div>
            <dt className="text-xs font-medium text-gray-500 uppercase tracking-wide">{t('projectReports.detail.projectEnd')}</dt>
            <dd className="mt-1 text-sm text-gray-900">{formatDate(report.project_end)}</dd>
          </div>
        </div>
      </div>

      {/* Content sections */}
      {report.content && (
        <section className="card">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{t('projectReports.detail.content')}</h2>
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: report.content }} />
        </section>
      )}

      {report.challenges && (
        <section className="card">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{t('projectReports.detail.challenges')}</h2>
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: report.challenges }} />
        </section>
      )}

      {report.next_steps && (
        <section className="card">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{t('projectReports.detail.nextSteps')}</h2>
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: report.next_steps }} />
        </section>
      )}

      {report.potentials && (
        <section className="card">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{t('projectReports.detail.potentials')}</h2>
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: report.potentials }} />
        </section>
      )}

      {report.lessons_learned && (
        <section className="card">
          <h2 className="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3">{t('projectReports.detail.lessonsLearned')}</h2>
          <div className="prose prose-sm max-w-none text-gray-700" dangerouslySetInnerHTML={{ __html: report.lessons_learned }} />
        </section>
      )}
    </div>
  );
}
